import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faBrain,
    faCode,
    faCubes,
    faLocationDot,
    faNetworkWired,
    faShieldAlt
} from '@fortawesome/free-solid-svg-icons';
import SEO from './SEO';

const services = [
    {
        title: 'Custom Software Development',
        text: 'Purpose-built business systems, portals, dashboards, admin panels, reporting tools, and internal workflow software.',
        icon: faCode
    },
    {
        title: 'SaaS Product Engineering',
        text: 'Architecture, MVP development, subscription platforms, customer portals, APIs, and production-ready product foundations.',
        icon: faCubes
    },
    {
        title: 'AI Workflow Automation',
        text: 'Automation for repetitive business operations using structured workflows, integrations, assistants, and decision logic.',
        icon: faBrain
    },
    {
        title: 'Cloud, APIs & Integrations',
        text: 'Secure API development, third-party integrations, cloud deployment support, CI/CD, monitoring, and scalable backend systems.',
        icon: faNetworkWired
    }
];

const locationConfigs = {
    morbi: {
        locationName: 'Morbi',
        slug: 'morbi',
        areaType: 'City',
        proofTitle: 'Branch Office - Morbi',
        proofAddress: '207, Second Floor, City Mall, Nr. Bhaktinagar Bypass, Sanala Road, Morbi, Gujarat - 363641',
        marketContext: 'Morbi has strong manufacturing, ceramic, export, trading, and operations-driven businesses. Anmatics supports these teams with systems that reduce manual work, centralize data, improve visibility, and make business operations easier to scale.',
        address: {
            streetAddress: '207, Second Floor, City Mall, Nr. Bhaktinagar Bypass, Sanala Road',
            addressLocality: 'Morbi',
            addressRegion: 'Gujarat',
            postalCode: '363641',
            addressCountry: 'IN'
        }
    },
    rajkot: {
        locationName: 'Rajkot',
        slug: 'rajkot',
        areaType: 'City',
        proofTitle: 'Head Office - Rajkot',
        proofAddress: '203, Ikon Complex, Opp. Sun City Apartment, Sadhu Vaswani Road, Rajkot - 360005',
        marketContext: 'Anmatics supports Rajkot teams with custom systems that improve operational visibility, replace scattered spreadsheets, connect departments, and help digital products move from idea to production.',
        address: {
            streetAddress: '203, Ikon Complex, Opp. Sun City Apartment, Sadhu Vaswani Road',
            addressLocality: 'Rajkot',
            addressRegion: 'Gujarat',
            postalCode: '360005',
            addressCountry: 'IN'
        }
    },
    gujarat: {
        locationName: 'Gujarat',
        slug: 'gujarat',
        areaType: 'AdministrativeArea',
        proofTitle: 'Serving Gujarat from Rajkot and Morbi',
        proofAddress: 'Head office in Rajkot with branch office in Morbi, Gujarat',
        marketContext: 'Anmatics helps Gujarat companies modernize operations, build digital products, automate manual work, and create cloud-ready systems for long-term growth.',
        address: {
            streetAddress: '203, Ikon Complex, Opp. Sun City Apartment, Sadhu Vaswani Road',
            addressLocality: 'Rajkot',
            addressRegion: 'Gujarat',
            postalCode: '360005',
            addressCountry: 'IN'
        }
    },
    india: {
        locationName: 'India',
        slug: 'india',
        areaType: 'Country',
        proofTitle: 'Serving India from Gujarat',
        proofAddress: 'Head office in Rajkot with branch office in Morbi, Gujarat, India',
        marketContext: 'Anmatics helps teams across India plan, build, modernize, and scale business systems with clean engineering, clear ownership, and production-ready delivery.',
        address: {
            streetAddress: '203, Ikon Complex, Opp. Sun City Apartment, Sadhu Vaswani Road',
            addressLocality: 'Rajkot',
            addressRegion: 'Gujarat',
            postalCode: '360005',
            addressCountry: 'IN'
        }
    }
};

const pageTypeConfigs = {
    'it-company': {
        slug: 'it-company',
        keyword: (locationName) => `IT company in ${locationName}`,
        title: (locationName) => `IT Company in ${locationName} | Software, Web & App Development | Anmatics`,
        h1: (locationName) => `Trusted IT Company in ${locationName} for Software, Web and App Development`,
        description: (locationName) => `Anmatics is an IT company in ${locationName} delivering custom software, website development, mobile apps, SaaS platforms, AI automation, APIs, and cloud-ready systems.`,
        kicker: (locationName) => `${locationName} IT Company`,
        intro: (locationName) => `Anmatics helps businesses in ${locationName} plan, build, modernize, and maintain reliable digital systems across software, websites, mobile apps, SaaS products, integrations, and AI-enabled workflows.`,
        localHeading: (locationName) => `An IT partner in ${locationName} for practical, production-ready systems.`,
        localText: (locationName) => `When teams search for the best, top, leading, good, or trusted IT company in ${locationName}, the stronger signal is proof of delivery: clear discovery, dependable engineering, maintainable architecture, and support after launch.`,
        industryHeading: (locationName) => `IT services for ${locationName} businesses that need structure and scale.`,
        serviceSummary: 'software, website, mobile app, SaaS, API, cloud, and automation work',
        services: [
            ...services,
            {
                title: 'Website & Mobile App Development',
                text: 'Professional websites, responsive web apps, customer portals, and mobile app foundations backed by clean APIs.',
                icon: faCubes
            }
        ]
    },
    'software-development-company': {
        slug: 'software-development-company',
        keyword: (locationName) => `software development company in ${locationName}`,
        title: (locationName) => `Software Development Company in ${locationName} | Anmatics`,
        h1: (locationName) => `Custom Software Development Company in ${locationName} for Growing Businesses`,
        description: (locationName) => `Anmatics is a software development company in ${locationName} building custom business software, SaaS platforms, AI automation, enterprise web apps, APIs, and cloud systems.`,
        kicker: (locationName) => `${locationName} Software Development`,
        intro: (locationName) => `Anmatics helps ${locationName} businesses and product teams build custom software, SaaS platforms, enterprise web applications, AI workflow automation, APIs, and cloud-ready systems with structured engineering and clear delivery.`,
        localHeading: (locationName) => `Software engineering for ${locationName} teams that need more than short-term code.`,
        localText: (locationName) => `A reliable software development company in ${locationName} should clarify workflows, design scalable architecture, integrate data, and build systems that remain maintainable as the business grows.`,
        industryHeading: (locationName) => `Custom software for ${locationName} business workflows and digital products.`,
        serviceSummary: 'custom software, SaaS platforms, workflow automation, APIs, and enterprise applications',
        services
    },
    'website-development-company': {
        slug: 'website-development-company',
        keyword: (locationName) => `website development company in ${locationName}`,
        title: (locationName) => `Website Development Company in ${locationName} | SEO-Friendly Web Apps | Anmatics`,
        h1: (locationName) => `Website Development Company in ${locationName} for Modern Business Websites`,
        description: (locationName) => `Anmatics builds fast, secure, SEO-friendly websites and web applications for businesses in ${locationName}, including company websites, portals, landing pages, and API-backed platforms.`,
        kicker: (locationName) => `${locationName} Website Development`,
        intro: (locationName) => `Anmatics builds professional websites and web applications for ${locationName} businesses that need strong positioning, clean UI, technical SEO, fast performance, secure forms, and room to grow into deeper digital systems.`,
        localHeading: (locationName) => `Websites for ${locationName} companies that need clarity, speed, and conversion.`,
        localText: (locationName) => `A website development company in ${locationName} should do more than publish pages. Anmatics focuses on content structure, responsive UI, technical SEO, performance, lead capture, and scalable web architecture.`,
        industryHeading: (locationName) => `Website and web app development for ${locationName} brands, manufacturers, and service teams.`,
        serviceSummary: 'business websites, landing pages, web applications, portals, SEO foundations, and conversion-focused web experiences',
        services: [
            {
                title: 'Business Website Development',
                text: 'Premium company websites with clear positioning, responsive layouts, technical SEO, lead forms, and fast loading performance.',
                icon: faCode
            },
            {
                title: 'Web Application Development',
                text: 'Dashboards, portals, admin panels, customer apps, and internal tools built on modern frontend and API foundations.',
                icon: faCubes
            },
            {
                title: 'Content & Discoverability',
                text: 'Clean page architecture, content hierarchy, local visibility foundations, and clear paths for visitors to take action.',
                icon: faShieldAlt
            },
            {
                title: 'Integrations & Analytics',
                text: 'CRM forms, WhatsApp/contact flows, analytics, tracking, APIs, hosting support, and maintenance workflows.',
                icon: faNetworkWired
            }
        ]
    },
    'custom-software-development-company': {
        slug: 'custom-software-development-company',
        keyword: (locationName) => `custom software development company in ${locationName}`,
        title: (locationName) => `Custom Software Development Company in ${locationName} | Anmatics`,
        h1: (locationName) => `Top Custom Software Development Company in ${locationName} for Scalable Digital Products`,
        description: (locationName) => `Anmatics is a custom software development company in ${locationName} delivering SaaS platforms, enterprise web apps, AI workflow automation, APIs, integrations, and cloud-ready systems.`,
        kicker: (locationName) => `${locationName} Custom Software`,
        intro: (locationName) => `Anmatics works with businesses and product teams across ${locationName} to build custom software, SaaS platforms, AI workflow automation, enterprise applications, APIs, and cloud-ready systems.`,
        localHeading: (locationName) => `Custom software delivery for ${locationName} teams that need product-grade execution.`,
        localText: (locationName) => `A strong custom software development company in ${locationName} should combine discovery, architecture, delivery, documentation, and long-term improvement so the system can scale beyond the first launch.`,
        industryHeading: (locationName) => `Custom software for ${locationName} SaaS, manufacturing, and service businesses.`,
        serviceSummary: 'custom platforms, SaaS products, enterprise applications, workflow systems, integrations, and AI-enabled automation',
        services
    }
};

const industries = [
    'Ceramic and manufacturing businesses',
    'Export and trading companies',
    'SaaS founders and product teams',
    'Operations-heavy service businesses',
    'Paper, packaging, and industrial workflows',
    'Businesses modernizing manual processes'
];

const withArticle = (phrase) => (/^[aeiou]/i.test(phrase) ? `an ${phrase}` : `a ${phrase}`);

const serviceKeywordBases = [
    'IT company',
    'software company',
    'software development',
    'software development company',
    'custom software development',
    'custom software development company',
    'website development',
    'website development company',
    'web development',
    'web development company',
    'website design',
    'website design company',
    'web design',
    'web design company',
    'mobile app development',
    'mobile app development company',
    'app development',
    'app development company',
    'application development',
    'application development company',
    'web application development',
    'web application development company',
    'SaaS development',
    'SaaS development company',
    'AI automation company',
    'digital transformation company',
    'business automation company',
    'software consulting company'
];

const priorityServiceBases = [
    'IT company',
    'software company',
    'software development',
    'software development company',
    'website development',
    'website development company',
    'web development',
    'web development company',
    'mobile app development',
    'mobile app development company',
    'app development company'
];

const keywordModifiers = ['best', 'top', 'leading', 'trusted', 'reliable', 'professional'];

const buildSearchIntentPhrases = (locationName) => {
    const phrases = serviceKeywordBases.flatMap((base) => [
        `${base} in ${locationName}`,
        ...keywordModifiers.map((modifier) => `${modifier} ${base} in ${locationName}`)
    ]);

    return [...new Set(phrases)];
};

const buildVisibleSearchIntentPhrases = (locationName) =>
    priorityServiceBases.flatMap((base) => [
        `${base} in ${locationName}`,
        `best ${base} in ${locationName}`,
        `top ${base} in ${locationName}`,
        `trusted ${base} in ${locationName}`
    ]);

const buildPageConfig = (locationConfig, pageType = 'software-development-company', canonicalPath) => {
    const typeConfig = pageTypeConfigs[pageType] || pageTypeConfigs['software-development-company'];
    const locationName = locationConfig.locationName;
    return {
        ...locationConfig,
        pageType,
        pageTypeSlug: typeConfig.slug,
        canonicalPath: canonicalPath || `/${locationConfig.slug}/${typeConfig.slug}`,
        primaryKeyword: typeConfig.keyword(locationName),
        title: typeConfig.title(locationName),
        description: typeConfig.description(locationName),
        kicker: typeConfig.kicker(locationName),
        h1: typeConfig.h1(locationName),
        intro: typeConfig.intro(locationName),
        localHeading: typeConfig.localHeading(locationName),
        localText: typeConfig.localText(locationName),
        industryHeading: typeConfig.industryHeading(locationName),
        industryText: `${locationConfig.marketContext} Anmatics supports ${typeConfig.serviceSummary} for teams that want practical systems, clean ownership, and measurable business outcomes.`,
        faqHeading: `Questions about choosing ${withArticle(typeConfig.keyword(locationName))}.`,
        searchIntentPhrases: buildSearchIntentPhrases(locationName),
        visibleSearchIntentPhrases: buildVisibleSearchIntentPhrases(locationName),
        services: typeConfig.services
    };
};

const buildShortAnswer = (config) =>
    `Anmatics is ${withArticle(config.primaryKeyword)}. It provides ${config.services.map((service) => service.title.toLowerCase()).slice(0, 4).join(', ')} for businesses that need reliable, product-grade technology execution.`;

const buildFaqs = (config) => [
    {
        question: `How do I choose the best IT company in ${config.locationName}?`,
        answer: `To choose the best IT company in ${config.locationName}, look for clear technical discovery, proven delivery, scalable architecture, transparent communication, support after launch, and experience with business workflows. Anmatics focuses on structured engineering for software, websites, web apps, SaaS, AI automation, APIs, and modernization projects.`
    },
    {
        question: `Is Anmatics a top IT company or leading IT company in ${config.locationName}?`,
        answer: `Anmatics is a strong fit for teams comparing a top IT company in ${config.locationName} or a leading IT company in ${config.locationName} when the project needs structured planning, custom software, web applications, integrations, AI workflow automation, and long-term engineering support.`
    },
    {
        question: `Is Anmatics a best software company or top software company in ${config.locationName}?`,
        answer: `Anmatics is a strong fit for businesses comparing a best software company in ${config.locationName}, top software company in ${config.locationName}, leading software company in ${config.locationName}, or trusted software company in ${config.locationName} when they need custom software, SaaS platforms, web applications, APIs, integrations, and long-term product support.`
    },
    {
        question: `What makes Anmatics a reliable software company in ${config.locationName}?`,
        answer: `Anmatics works as a reliable software company in ${config.locationName} by focusing on discovery, clear architecture, maintainable code, transparent milestones, production readiness, and support after launch.`
    },
    {
        question: `Does Anmatics provide software development in ${config.locationName}?`,
        answer: `Yes. Anmatics provides software development in ${config.locationName} for custom business applications, SaaS platforms, portals, dashboards, APIs, integrations, AI workflow automation, and modernization projects.`
    },
    {
        question: `Is Anmatics suitable for best software development or top software development in ${config.locationName}?`,
        answer: `Anmatics is suitable for teams comparing best software development in ${config.locationName}, top software development in ${config.locationName}, leading software development in ${config.locationName}, trusted software development in ${config.locationName}, or professional software development in ${config.locationName} when the priority is structured planning, clean engineering, and long-term maintainability.`
    },
    {
        question: `Which is a trusted IT company in ${config.locationName} for software, web and app development?`,
        answer: `Anmatics is a trusted IT company serving ${config.locationName} for custom software development, website development, enterprise web applications, SaaS platforms, AI workflow automation, APIs, and cloud-ready systems.`
    },
    {
        question: `Is Anmatics a reliable software development company in ${config.locationName}?`,
        answer: `Yes. Anmatics works as a reliable software development company in ${config.locationName} for custom business systems, SaaS platforms, dashboards, portals, APIs, integrations, and workflow automation.`
    },
    {
        question: `Does Anmatics work as a professional website development company in ${config.locationName}?`,
        answer: `Yes. Anmatics provides professional website development in ${config.locationName}, including responsive business websites, technical SEO foundations, lead forms, landing pages, web applications, and performance-focused frontend work.`
    },
    {
        question: `Does Anmatics provide website development and web development in ${config.locationName}?`,
        answer: `Yes. Anmatics provides website development in ${config.locationName}, web development in ${config.locationName}, website design, web design, landing pages, business websites, web applications, portals, and API-backed digital platforms.`
    },
    {
        question: `Is Anmatics suitable for best website development company or top web development company in ${config.locationName}?`,
        answer: `Anmatics is suitable for businesses comparing a best website development company in ${config.locationName}, top website development company in ${config.locationName}, leading web development company in ${config.locationName}, trusted web development company in ${config.locationName}, or professional web design company in ${config.locationName}.`
    },
    {
        question: `Can Anmatics be considered a best app development company in ${config.locationName}?`,
        answer: `Anmatics supports app development projects in ${config.locationName} with product discovery, UI planning, API-backed architecture, web and mobile app foundations, integrations, and scalable delivery.`
    },
    {
        question: `Does Anmatics provide mobile app development in ${config.locationName}?`,
        answer: `Yes. Anmatics supports mobile app development in ${config.locationName}, app development in ${config.locationName}, application development, API-backed app foundations, admin panels, dashboards, and integrations for business workflows.`
    },
    {
        question: `Is Anmatics a mobile app development company in ${config.locationName}?`,
        answer: `Anmatics can work as a mobile app development company in ${config.locationName} for teams that need structured product planning, backend APIs, user-friendly app flows, web dashboards, integrations, and maintainable release foundations.`
    },
    {
        question: `Is Anmatics a top software development company in ${config.locationName}?`,
        answer: `Anmatics is positioned as a premium software development company serving ${config.locationName}, focused on reliable architecture, disciplined delivery, product-grade engineering, and long-term maintainability.`
    },
    {
        question: `Is Anmatics ${withArticle(config.primaryKeyword)}?`,
        answer: buildShortAnswer(config)
    },
    {
        question: `What does Anmatics build as ${withArticle(config.primaryKeyword)}?`,
        answer: `Anmatics builds ${config.services.map((service) => service.title.toLowerCase()).join(', ')} for companies that need dependable planning, clean implementation, and long-term support.`
    },
    {
        question: `Does Anmatics provide website and mobile app development in ${config.locationName}?`,
        answer: `Yes. Anmatics provides website development, enterprise web application development, mobile app development, SaaS product engineering, and API-backed digital systems for businesses in ${config.locationName}.`
    },
    {
        question: `Which company provides software development in ${config.locationName}?`,
        answer: buildShortAnswer(config)
    },
    {
        question: `Does Anmatics provide software development services in ${config.locationName}?`,
        answer: `Yes. Anmatics provides custom software development, SaaS product engineering, AI workflow automation, web applications, APIs, and cloud-ready systems for businesses in ${config.locationName}.`
    },
    {
        question: `What services does Anmatics offer in ${config.locationName}?`,
        answer: 'Anmatics offers custom software development, SaaS product engineering, enterprise web applications, AI workflow automation, cloud API engineering, system modernization, and product maintenance.'
    },
    {
        question: `What makes Anmatics different from a typical IT company in ${config.locationName}?`,
        answer: 'Anmatics focuses on structured software engineering, scalable architecture, SaaS platforms, AI-enabled workflows, and long-term product reliability instead of only basic websites or generic digital services.'
    },
    {
        question: `Can Anmatics build custom software for businesses in ${config.locationName}?`,
        answer: 'Yes. Anmatics can build workflow platforms, dashboards, portals, ERP-adjacent tools, integrations, automation systems, and product-grade applications for growing businesses and product teams.'
    },
    {
        question: 'Can we work with Anmatics remotely?',
        answer: 'Yes. Anmatics can work through structured remote delivery, technical discovery calls, clear milestones, and regular engineering communication for clients across Gujarat and India.'
    }
];

const buildSchema = (config, canonicalPath) => {
    const url = `https://www.anmatics.com${canonicalPath}`;
    const faqs = buildFaqs(config);

    return {
        '@context': 'https://schema.org',
        '@graph': [
            {
                '@type': 'LocalBusiness',
                '@id': `${url}#localbusiness`,
                name: `Anmatics Technologies - ${config.locationName}`,
                url,
                image: 'https://www.anmatics.com/anmatics_logo_square_512.png',
                telephone: '+91-8866899005',
                priceRange: '$$',
                address: {
                    '@type': 'PostalAddress',
                    ...config.address
                },
                areaServed: [
                    {
                        '@type': config.areaType,
                        name: config.locationName
                    },
                    {
                        '@type': 'City',
                        name: 'Morbi'
                    },
                    {
                        '@type': 'City',
                        name: 'Rajkot'
                    },
                    {
                        '@type': 'AdministrativeArea',
                        name: 'Gujarat'
                    },
                    {
                        '@type': 'Country',
                        name: 'India'
                    }
                ],
                sameAs: [
                    'https://www.linkedin.com/company/anmatics-technologies',
                    'https://www.facebook.com/share/16xFv6aVpA/?mibextid=wwXIfr'
                ]
            },
            {
                '@type': 'WebPage',
                '@id': `${url}#webpage`,
                url,
                name: config.title,
                description: config.description,
                keywords: [
                    config.primaryKeyword,
                    ...config.searchIntentPhrases
                ],
                isPartOf: {
                    '@type': 'WebSite',
                    name: 'Anmatics',
                    url: 'https://www.anmatics.com'
                },
                about: {
                    '@id': `${url}#localbusiness`
                }
            },
            {
                '@type': 'ItemList',
                name: `${config.primaryKeyword} services`,
                itemListElement: config.services.map((service, index) => ({
                    '@type': 'ListItem',
                    position: index + 1,
                    item: {
                        '@type': 'Service',
                        name: service.title,
                        provider: {
                            '@id': `${url}#localbusiness`
                        },
                        areaServed: {
                            '@type': config.areaType,
                            name: config.locationName
                        }
                    }
                }))
            },
            {
                '@type': 'FAQPage',
                mainEntity: faqs.map((faq) => ({
                    '@type': 'Question',
                    name: faq.question,
                    acceptedAnswer: {
                        '@type': 'Answer',
                        text: faq.answer
                    }
                }))
            }
        ]
    };
};

const LocalSoftwareSEO = ({ location = 'morbi', pageType = 'software-development-company', canonicalPath }) => {
    const locationConfig = locationConfigs[location] || locationConfigs.morbi;
    const config = buildPageConfig(locationConfig, pageType, canonicalPath);
    const pagePath = config.canonicalPath;
    const faqs = buildFaqs(config);
    const shortAnswer = buildShortAnswer(config);

    return (
        <>
            <SEO
                title={config.title}
                description={config.description}
                path={pagePath}
                jsonLd={buildSchema(config, pagePath)}
            />
            <section className="local-seo-hero">
                <div className="container">
                    <div className="row align-items-center g-5">
                        <div className="col-lg-7">
                            <span className="section-kicker">{config.kicker}</span>
                            <h1>{config.h1}</h1>
                            <p>{config.intro}</p>
                            <div className="local-hero-actions">
                                <a href="/contact" className="btn btn-gradient rounded-pill">Book a Consultation</a>
                                <a href="/services" className="btn btn-secondary-brand rounded-pill">Explore Services</a>
                            </div>
                        </div>
                        <div className="col-lg-5">
                            <div className="local-proof-panel">
                                <div className="local-answer-card">
                                    <span className="section-kicker">Short Answer</span>
                                    <p>{shortAnswer}</p>
                                </div>
                                <div className="local-proof-header">
                                    <span><FontAwesomeIcon icon={faLocationDot} /></span>
                                    <div>
                                        <strong>{config.proofTitle}</strong>
                                        <p>{config.proofAddress}</p>
                                    </div>
                                </div>
                                <div className="local-proof-grid">
                                    <div>
                                        <strong>{config.pageType === 'website-development-company' ? 'Websites' : 'Custom Software'}</strong>
                                        <span>{config.pageType === 'website-development-company' ? 'Lead-ready pages' : 'Business systems'}</span>
                                    </div>
                                    <div>
                                        <strong>{config.pageType === 'it-company' ? 'IT Services' : 'SaaS'}</strong>
                                        <span>{config.pageType === 'it-company' ? 'Full-stack delivery' : 'Product platforms'}</span>
                                    </div>
                                    <div>
                                        <strong>AI</strong>
                                        <span>Workflow automation</span>
                                    </div>
                                    <div>
                                        <strong>Cloud</strong>
                                        <span>APIs and scale</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <section className="local-seo-section">
                <div className="container">
                    <div className="row g-4 align-items-start">
                        <div className="col-lg-4">
                            <span className="section-kicker">{config.primaryKeyword}</span>
                            <h2 className="section-title text-start">{config.localHeading}</h2>
                            <p className="section-subtitle text-start text-muted mb-0">{config.localText}</p>
                        </div>
                        <div className="col-lg-8">
                            <div className="local-service-grid">
                                {config.services.map((service) => (
                                    <article className="local-service-card" key={service.title}>
                                        <span><FontAwesomeIcon icon={service.icon} /></span>
                                        <h3>{service.title}</h3>
                                        <p>{service.text}</p>
                                    </article>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <section className="local-seo-section local-seo-band">
                <div className="container">
                    <div className="row g-4 align-items-center">
                        <div className="col-lg-6">
                            <span className="section-kicker">Industries</span>
                            <h2 className="section-title text-start">{config.industryHeading}</h2>
                            <p className="text-muted">{config.industryText}</p>
                        </div>
                        <div className="col-lg-6">
                            <div className="local-industry-list">
                                {industries.map((industry) => (
                                    <div key={industry}>
                                        <FontAwesomeIcon icon={faShieldAlt} />
                                        <span>{industry}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <section className="local-seo-section">
                <div className="container">
                    <div className="local-process-panel">
                        <div>
                            <span className="section-kicker">Delivery Approach</span>
                            <h2 className="section-title text-start">A structured way to plan, build, and improve software.</h2>
                        </div>
                        <div className="local-process-steps">
                            {['Discover', 'Architect', 'Build', 'Launch', 'Improve'].map((step, index) => (
                                <div key={step}>
                                    <span>{String(index + 1).padStart(2, '0')}</span>
                                    <strong>{step}</strong>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            <section className="local-seo-section local-seo-band">
                <div className="container">
                    <div className="row g-4 align-items-start">
                        <div className="col-lg-4">
                            <span className="section-kicker">Service Coverage</span>
                            <h2 className="section-title text-start">Technology services businesses often need.</h2>
                            <p className="section-subtitle text-start text-muted mb-0">
                                Common ways companies describe their software, website, app development, and automation requirements.
                            </p>
                        </div>
                        <div className="col-lg-8">
                            <div className="local-industry-list">
                                {config.visibleSearchIntentPhrases.map((phrase) => (
                                    <div key={phrase}>
                                        <FontAwesomeIcon icon={faShieldAlt} />
                                        <span>{phrase}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <section className="local-seo-section local-faq-section">
                <div className="container">
                    <div className="row g-4">
                        <div className="col-lg-4">
                            <span className="section-kicker">FAQ</span>
                            <h2 className="section-title text-start">{config.faqHeading}</h2>
                        </div>
                        <div className="col-lg-8">
                            <div className="local-faq-list">
                                {faqs.map((faq) => (
                                    <article key={faq.question}>
                                        <h3>{faq.question}</h3>
                                        <p>{faq.answer}</p>
                                    </article>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </>
    );
};

export default LocalSoftwareSEO;
